import { Vue, Component, Prop, Watch, Ref } from "vue-property-decorator";
import { Upload, Icon, Button, Progress, List, Col, Row, Modal, Select, Empty } from "ant-design-vue";
import { MediaIconType, MediaPlayerContentType, MediaSlot, MediaStatus, MediaType } from "@/util/enum";
import { MediaUploaderLocale } from "@/locales/localeid";
import { getModule } from "vuex-module-decorators";
import { MediaUploadState } from "@/modules/media-upload";

import { HasPdfContentResponseModel, MediaFile, PageAndAudioMediaResponseModel, PageAudioModel, StatusType } from "@/services/media-uploader";
import { MediaSlotBinding, MediaSlotFileSizeBinding, MediaTypeBinding } from "../../../utils/const";
import { fmtMsg, LanguageLocale, MessageHelper, NotificationType, PrimaryLanguageLocale } from "commonui";
import { MediaNameToSlotMap, MediaSlotTypeIconTypeMap, MediaSlotTypeMap } from "@/util/constants";
import { flagIcons } from "@/style/flags";
import { DateHelper } from "@/util/helper";
import { PageSettings, ResourceService } from "@/services/resource";
import FlagIcon from "@/components/flag-icons/flag-icon.vue";
import MediaViewer from "../media-viewer/media-viewer.vue";
import MediaViewerTS from "../media-viewer/media-viewer";
import VideoJSPlayer from "@/components/videojsplayer/videojsplayer.vue";
import IVideoJSPlayer from "@/components/videojsplayer/videojsplayer";
import { v4 } from "uuid";
import { ContentService } from "@/services/content/service";

@Component({
	components: {
		Upload,
		UploadDragger: Upload.Dragger,
		Icon,
		Button,
		Progress,
		List,
		Row,
		Col,
		FlagIcon,
		MediaViewer,
		Modal,
		VideoJSPlayer,
		Select,
		SelectOption: Select.Option,
		Empty
	}
})
export default class FileUploader extends Vue {
	@Prop() fileItem?: MediaFile;
	@Prop() isUploadStarted?: boolean;
	@Prop() mediaSlotId?: MediaSlot;
	@Prop({ default: () => { } }) onFileUpload!: (mediaFile: MediaFile) => void;
	@Prop({ default: () => { } }) removeMedia!: (id: string) => void;
	@Prop({ default: "" }) mediaSlotStatus?: string;
	@Prop() mediaStatusId!: number;
	@Prop() currentComponentId!: string;
	@Prop() pdfSlots?: HasPdfContentResponseModel[];
	@Prop({ default: [] }) allFileItem?: MediaFile[];

	@Ref("mediaviewerRef") mediaviewerRef!: any;
	@Ref("videoPlayerRef") videoPlayerRef!: IVideoJSPlayer;

	mediaUploadStore = getModule(MediaUploadState);
	showProgress: boolean = false;
	public fileList = [];
	public fileName: string = "";
	public fileSize: string = "";

	public showDeleteOption: boolean = false;
	public showFileViewer: boolean = false;
	public uploadDate: string = "";
	public supportedMediaType: string = "";
	public isMediaViewerVisible: boolean = false;
	public isMediaStatusPublished: boolean = false;
	public confirmLoading: boolean = false;
	public isDirty: boolean = false;
	public slotList: string[] = [];
	public pdfMediaSlots = [MediaSlot.ViewingContent, MediaSlot.AudioBackground, MediaSlot.TeacherManual];
	public pageAndAudio: PageAudioModel = { pageInitialUrl: "", pageAudioMediaList: [] };
	public removedMediaList: string[] = [];
	public addedMediaList: any[] = [];
	public slotSelected: string = "";
	public isMediaUploaded: boolean = false;
	public previousSlot: string = "";
	public showWarning: boolean = false;
	public individualAudioCumuluativeStatus: boolean = false;

	@Watch("fileItem", { deep: true })
	setMediaStatus() {
		this.isMediaStatusPublished = this.isMediaPublished;
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	get flagType() {
		return PrimaryLanguageLocale.Arabic;
	}
	async downloadFile() {
		if (
			this.fileItem &&
			(this.mediaSlotId == MediaSlot.Audio ||
				this.mediaSlotId == MediaSlot.OfflineVideo ||
				this.mediaSlotId == MediaSlot.Thumbnail ||
				this.mediaSlotId == MediaSlot.AlternateContent ||
				this.mediaSlotId == MediaSlot.ViewingContent)
		) {
			let downloadMediaId!: string;

			if (this.fileItem.isExisting) {
				downloadMediaId = this.fileItem.id;
			} else if (this.fileItem.publishedMediaRevision && this.fileItem.publishedMediaRevision.mediaId) {
				downloadMediaId = this.fileItem.publishedMediaRevision.mediaId;
			}

			if (downloadMediaId) {
				const fileUrl = await ResourceService.getDownloadMediaUrl(downloadMediaId);
				const downloadLink = document.createElement("a");
				downloadLink.href = fileUrl;
				document.body.appendChild(downloadLink);
				downloadLink.click();
				document.body.removeChild(downloadLink);
			}
		}
	}

	get flagIcon() {
		if (this.fileItem) {
			switch (this.fileItem.language) {
				case PrimaryLanguageLocale.English:
					return flagIcons.en;
				case PrimaryLanguageLocale.Chinese:
					return flagIcons["zh-cn"];
				case PrimaryLanguageLocale.Arabic:
					return flagIcons["ar-sa"];
				case PrimaryLanguageLocale.Japanese:
					return flagIcons.ja;
				case PrimaryLanguageLocale.Korean:
					return flagIcons.ko;
				case PrimaryLanguageLocale.Malaysia:
					return flagIcons.ms;
				case PrimaryLanguageLocale.Mongolian:
					return flagIcons.mn;
				case PrimaryLanguageLocale.Russian:
					return flagIcons.ru;
				case PrimaryLanguageLocale.Spanish:
					return flagIcons.es;
				case PrimaryLanguageLocale.Thai:
					return flagIcons.th;
				case PrimaryLanguageLocale.Vietnamese:
					return flagIcons.vi;
				case PrimaryLanguageLocale.Myanmar:
					return flagIcons.my;
				case PrimaryLanguageLocale.Khmer:
					return flagIcons.km;
			}
		}
		return null;
	}

	get mediaUploaderLocale() {
		return MediaUploaderLocale;
	}

	getSupportedMediaType() {
		if (
			this.mediaSlotId == MediaSlot.AlternateContent ||
			this.mediaSlotId == MediaSlot.ViewingContent ||
			this.mediaSlotId == MediaSlot.TeacherManual ||
			this.mediaSlotId == MediaSlot.AudioBackground
		) {
			return ".pdf";
		} else if (this.mediaSlotId == MediaSlot.OfflineVideo || this.mediaSlotId == MediaSlot.StreamingVideo) {
			return ".mp4";
		} else if (this.mediaSlotId == MediaSlot.Audio || this.mediaSlotId == MediaSlot.IndividualAudio) {
			return ".mp3";
		} else if (this.mediaSlotId == MediaSlot.Thumbnail || this.mediaSlotId == MediaSlot.Image) {
			return ".png";
		}
		return "";
	}

	get slotIconType() {
		if (this.mediaSlotId) {
			return MediaSlotTypeIconTypeMap[this.mediaSlotId];
		}
	}

	get languageText() {
		if (this.fileItem && this.fileItem.language) {
			return LanguageLocale[this.fileItem.language];
		}
		return "";
	}

	get languageFlag() {
		return `@/assets/flags/${this.fileItem?.language}.svg`;
	}

	get isCompletedFileUpload() {
		const uploadInfo = this.mediaUploadStore.fileUploads.find((f) => f.id == this.fileItem?.id);
		if (uploadInfo) {
			return uploadInfo.status == StatusType.Completed;
		}
		return false;
	}

	getFormattedFileSize(sizeInMb: number) {
		if (sizeInMb < 1) {
			return `${(sizeInMb * 1024).toFixed(2)} KB`;
		}
		return `${sizeInMb.toFixed(2)} MB`;
	}

	@Watch("fileItem", { immediate: true, deep: true })
	showDeleteOptionWatcher() {
		this.supportedMediaType = this.getSupportedMediaType();
		if (this.fileItem) {
			if (
				(this.fileItem.publishedMediaRevision && this.fileItem.publishedMediaRevision.mediaId) ||
				this.fileItem.isExisting ||
				this.fileItem.file
			) {
				this.showDeleteOption = true;
			}
			if (this.fileItem.fileName) {
				this.fileName = this.fileItem.fileName;
				this.isUploadStarted = true;
			}
			if (this.fileItem.size) {
				this.fileSize = this.getFormattedFileSize(this.fileItem.size);
			}

			if (this.fileItem.uploadedAt) {
				this.uploadDate = DateHelper.formatDateTime2Local(this.fileItem.uploadedAt as string);

				// if (this.mediaSlotId == MediaSlot.Audio || this.mediaSlotId == MediaSlot.OfflineVideo) {
				// 	this.showFileViewer = true;
				// }
				this.showFileViewer = true;
			} else if (this.fileItem.publishedMediaRevision && this.fileItem.publishedMediaRevision.mediaId) {
				this.showFileViewer = true;
				if (this.fileItem.publishedMediaRevision.uploadedAt) {
					this.uploadDate = DateHelper.formatDateTime2Local(
						this.fileItem.publishedMediaRevision.uploadedAt as string
					);
				}
			}

			this.uploadDate = this.uploadDate ? this.uploadDate.substr(0, this.uploadDate.lastIndexOf(" ")) : "";
		}
	}

	dummyRequest({ onSuccess }) {
		setTimeout(() => {
			onSuccess("OK");
		});
	}

	beforeUpload(file: File) {
		this.fileName = "";
		this.fileSize = "";
		this.showProgress = false;
		const fileNameSplit = file.name.split(".");
		const fileType = fileNameSplit[fileNameSplit.length - 1];

		if (this.supportedMediaType && this.supportedMediaType.indexOf(fileType) < 0) {
			MessageHelper.Message(NotificationType.Warning, "Please select " + this.supportedMediaType + " file");
			return false;
		}

		if (this.mediaSlotId && file.size > MediaSlotFileSizeBinding[this.mediaSlotId]) {
			MessageHelper.Message(NotificationType.Warning, `${file.name} file size exceeded.`);
			return false;
		}
		this.fileName = file.name;
		this.fileSize = file.size ? this.getFormattedFileSize(file.size / 1024 / 1024) : "";
	}

	onUploadChange(info) {
		if (!info.file || info.file.status !== "done") {
			return false;
		} else if (info.file.status === "error") {
			MessageHelper.Message(NotificationType.Warning, `${info.file.name} file upload failed.`);
			return false;
		}

		let browserFile: File;
		const { originFileObj } = info.file as any;

		const blob = new Blob([originFileObj], { type: info.file.type });

		if (this.fileItem) {
			if (this.slotIconType == MediaIconType.Audio || this.slotIconType == MediaIconType.Video) {
				const fileUrl = URL.createObjectURL(blob);
				const vidoeRef = document.createElement("video");
				vidoeRef.setAttribute("src", fileUrl);
				vidoeRef.setAttribute("id", this.fileItem.id);
				const that = this;
				vidoeRef.addEventListener("loadedmetadata", function () {
					if (that.fileItem) {
						that.fileItem.length = vidoeRef.duration;
						document.body.removeChild(vidoeRef);
					}
				});
				document.body.appendChild(vidoeRef);
			} else {
				this.fileItem.length = undefined;
			}
		}

		if (!navigator.msSaveBlob) {
			browserFile = new File([originFileObj], `${info.file.name}`, { type: info.file.type });
		} else {
			browserFile = this.blobToFile(blob, `${info.file.name}`);
		}

		if (this.fileItem) {
			this.fileItem.file = browserFile;
			this.onFileUpload(this.fileItem);
		}
		this.showProgress = true;
		this.fileList = info.fileList;

		this.showDeleteOption = true;
		this.showFileViewer = true;
	}

	onModalOk() {
		if (this.previousSlot) {
			this.slotSelected = this.previousSlot;
		}
		this.showWarning = false;
	}

	onUploadMedia(fileInfo, mediaInfo) {
		if (!fileInfo.file || fileInfo.file.status !== "done") {
			return false;
		} else if (fileInfo.file.status === "error") {
			MessageHelper.Message(NotificationType.Warning, `${fileInfo.file.name} file upload failed.`);
			return false;
		}

		let browserFile: File;
		const { originFileObj } = fileInfo.file as any;

		const blob = new Blob([originFileObj], { type: fileInfo.file.type });

		let file;
		// audio exists
		if (mediaInfo.individualAudioMediaId !== '00000000-0000-0000-0000-000000000000') {
			if (mediaInfo.individualAudioStatus != MediaStatus.Published) {
				file = this.allFileItem ? this.allFileItem.find(f => f.id === mediaInfo.individualAudioMediaId) : null;
			}
			else {
				file = this.allFileItem ? this.allFileItem.find(f => f.publishedMediaRevision?.mediaId === mediaInfo.individualAudioMediaId) : null;
			}
		}
		// new audio
		else {
			file = this.allFileItem ? this.allFileItem.find(f => f.pageId === mediaInfo.pageId) : null;
		}

		if (file) {
			if (this.slotIconType == MediaIconType.Audio || this.slotIconType == MediaIconType.Video) {
				const fileUrl = URL.createObjectURL(blob);
				const vidoeRef = document.createElement("video");
				vidoeRef.setAttribute("src", fileUrl);
				vidoeRef.setAttribute("id", file.id);
				const that = this;
				vidoeRef.addEventListener("loadedmetadata", function () {
					if (that.fileItem) {
						that.fileItem.length = vidoeRef.duration;
						document.body.removeChild(vidoeRef);
					}
				});
				document.body.appendChild(vidoeRef);
			} else {
				file.length = undefined;
			}
		}

		if (!navigator.msSaveBlob) {
			browserFile = new File([originFileObj], `${fileInfo.file.name}`, { type: fileInfo.file.type });
		} else {
			browserFile = this.blobToFile(blob, `${fileInfo.file.name}`);
		}

		if (file) {
			file.file = browserFile;
			var addMedia = {
				filename: file.file.name,
				pageId: file.pageId
			};
			this.addedMediaList.push(addMedia);
			this.onFileUpload(file);
			this.isMediaUploaded = true;
			this.previousSlot = this.slotSelected;
		}
		this.showProgress = true;
		this.fileList = fileInfo.fileList;

		this.showDeleteOption = true;
		this.showFileViewer = true;
	}

	blobToFile(theBlob: Blob, fileName: string): File {
		const b: any = theBlob;
		b.name = fileName;
		return b as File;
	}

	getfileUploadProgress() {
		const uploadInfo = this.mediaUploadStore.fileUploads.find((f) => f.id === this.fileItem?.id);
		if (uploadInfo) {
			return Number.parseInt(uploadInfo.progress.toFixed(0));
		}
		return 0;
	}

	resetFile() {
		this.showDeleteOption = false;
		this.showProgress = false;
		this.showFileViewer = false;

		if (this.fileItem) {
			this.fileItem.file = undefined;
			this.fileItem.length = undefined;
			this.fileItem.progress = undefined;
			this.fileItem.downloadUrl = undefined;
			// this.fileItem.publishedMediaRevision = undefined;
			this.fileName = "";
			this.fileSize = "";
		}
	}
	removeMediaFile() {
		if (this.fileItem) {
			const id = this.fileItem.publishedMediaRevision
				? this.fileItem.publishedMediaRevision.mediaId
				: this.fileItem.id;

			this.resetFile();
			this.removeMedia(id);
		}
	}

	removeActionButtons(mediaId) {
		return this.removedMediaList.find(rm => rm === mediaId);
	}

	removeIndividualAudio(mediaId) {
		this.previousSlot = this.slotSelected;
		this.isMediaUploaded = true;
		this.resetFile();
		this.removedMediaList.push(mediaId);
		this.removeMedia(mediaId);
	}

	showFilenameOfUploadedFiles(pageId) {
		if (this.addedMediaList.length) {
			const newMediaUploaded = this.addedMediaList.find(am => am.pageId === pageId);
			if (newMediaUploaded) {
				return newMediaUploaded.filename;
			}
		}
		return "";
	}

	getIndividualAudioMediaStatus(mediaStatus) {
		if (mediaStatus === MediaStatus.Processing) {
			return "Processing";
		} else if (mediaStatus === MediaStatus.Queued) {
			return "Queued";
		} else if (mediaStatus === MediaStatus.Scheduled) {
			return "Scheduled";
		} else if (mediaStatus === MediaStatus.Published) {
			return "Published";
		} else if (mediaStatus === MediaStatus.Archived) {
			return "Archived";
		}
	}

	getIndividualAudioFileName(filename) {
		return filename;
	}

	isIndividualAudioMediaPublished(mediaId) {
		return mediaId === MediaStatus.Published;
	}

	get mediaUploadStatus() {
		const status = this.mediaStatusId;
		if (status === MediaStatus.Processing) {
			return "Processing";
		} else if (status === MediaStatus.Queued) {
			return "Queued";
		} else if (status === MediaStatus.Scheduled) {
			return "Scheduled";
		} else if (status === MediaStatus.Published) {
			return "Published";
		} else if (status === MediaStatus.Archived) {
			return "Archived";
		}
	}

	get isMediaPublished() {
		if (this.mediaStatusId === MediaStatus.Published) {
			return true;
		}

		return false;
	}

	get isMediaProcessing() {
		if (this.mediaStatusId === MediaStatus.Processing) {
			return true;
		}

		return false;
	}

	async toggleMediaViewer() {
		if (this.isMediaProcessing) {
			return;
		}
		if (this.mediaSlotId) {
			if (this.mediaSlotId === MediaSlot.AlternateContent) {
				return;
			} else {
				const mediaType = MediaSlotTypeMap[this.mediaSlotId];
				if (mediaType === MediaType.PDF || mediaType === MediaType.PNG) {
					if (this.isMediaViewerVisible) {
						this.isMediaViewerVisible = false;
					} else {
						this.isMediaViewerVisible = true;
					}
				}
				if (mediaType === MediaType.MP3 || mediaType === MediaType.MP4) {
					let downloadMediaId!: string;
					if (this.fileItem?.isExisting) {
						downloadMediaId = this.fileItem.id;
					} else if (this.fileItem?.publishedMediaRevision && this.fileItem?.publishedMediaRevision.mediaId) {
						downloadMediaId = this.fileItem?.publishedMediaRevision.mediaId;
					}

					if (downloadMediaId) {
						const fileUrl = await ResourceService.getDownloadMediaUrl(downloadMediaId);
						const result = (this.$refs.videoPlayerRef as IVideoJSPlayer).showModal(
							downloadMediaId,
							mediaType,
							this.formatMessage(this.mediaUploaderLocale.MediaUploadMediaViewerText)
						);
						if (result) {
							setTimeout(() => {
								(this.$refs.videoPlayerRef as IVideoJSPlayer).setAWSMediaPlayer(
									downloadMediaId,
									fileUrl,
									mediaType === MediaType.MP3
										? MediaPlayerContentType.mp3
										: MediaPlayerContentType.mp4Offline
								);
							}, 100);
						}
					}
				}
			}
		}
	}

	async toggleIndividualAudio(mediaId) {
		if (mediaId) {
			const fileUrl = await ResourceService.getDownloadMediaUrl(mediaId);
			const result = (this.$refs.videoPlayerRef as IVideoJSPlayer).showModal(
				mediaId,
				MediaType.MP3,
				this.formatMessage(this.mediaUploaderLocale.MediaUploadMediaViewerText)
			);
			if (result) {
				setTimeout(() => {
					(this.$refs.videoPlayerRef as IVideoJSPlayer).setAWSMediaPlayer(
						mediaId,
						fileUrl,
						MediaPlayerContentType.mp3
					);
				}, 100);
			}
		}
	}

	async onMediaViewerSave() {
		this.confirmLoading = true;

		const pageSettings: PageSettings[] = (this.$refs.mediaviewerRef as MediaViewerTS).pages.pageImages.map((page) => {
			return { pageId: page.id, teacherUseOnly: page.teacherUseOnly };
		});

		await ResourceService.savePageSettings(pageSettings);

		this.onMediaViewerClose();
	}

	onMediaViewerClose() {
		this.confirmLoading = false;
		this.isDirty = false;
		this.isMediaViewerVisible = false;
	}

	get isMediaTypePNG() {
		if (this.mediaSlotId) {
			const mediaType = MediaSlotTypeMap[this.mediaSlotId];
			if (mediaType === MediaType.PNG) {
				return null;
			}
		}
	}

	get isMediaAlternateContent() {
		if (this.mediaSlotId) {
			if (this.mediaSlotId === MediaSlot.AlternateContent) {
				return true;
			}
			return false;
		}
	}

	isModalDirty(isDirty) {
		this.isDirty = isDirty.isDirty;
	}

	get getMediaTypeName() {
		return this.mediaSlotId ? this.$format(MediaTypeBinding[MediaSlotTypeMap[this.mediaSlotId]]) : "";
	}

	isIndividualAudio() {
		return this.mediaSlotId == MediaSlot.IndividualAudio;
	}

	isIndividualAudioMediaEmpty(mediaId) {
		return mediaId === '00000000-0000-0000-0000-000000000000';
	}

	hasIndividualAudioMedia() {
		return this.pageAndAudio.pageAudioMediaList.some(p => !this.isIndividualAudioMediaEmpty(p.individualAudioMediaId));
	}

	get mediaUploadStatusForIndividualAudio() {
		const medias = this.pageAndAudio.pageAudioMediaList.filter(p => !this.isIndividualAudioMediaEmpty(p.individualAudioMediaId));
		if (!medias.length) {
			return "";
		}
		const allStatuses = medias.map(m => m.individualAudioStatus);
		const minimumStatus = this.calculateMediaStatus(allStatuses);
		this.individualAudioCumuluativeStatus = this.isIndividualAudioMediaPublished(minimumStatus);
		return this.individualAudioMediaUploadStatus(minimumStatus);
	}

	calculateMediaStatus(allStatuses) {
		const minStatus = Math.min(...allStatuses);
		return minStatus;
	}

	individualAudioMediaUploadStatus(mediaStatus) {
		if (mediaStatus === MediaStatus.Processing) {
			return "Processing";
		} else if (mediaStatus === MediaStatus.Queued) {
			return "Queued";
		} else if (mediaStatus === MediaStatus.Scheduled) {
			return "Scheduled";
		} else if (mediaStatus === MediaStatus.Published) {
			return "Published";
		} else if (mediaStatus === MediaStatus.Archived) {
			return "Archived";
		}
	}

	getInitialFileObj(info, languageCode, isMultilingual = false) {
		return {
			id: v4(),
			language: languageCode,
			isMultilingual: isMultilingual,
			isExisting: false,
			publishedMediaRevision: undefined,
			progress: undefined,
			uploadedAt: undefined,
			length: undefined,
			pageId: info.pageId
		} as MediaFile;
	}

	async mounted() {
		if (this.mediaSlotId === MediaSlot.IndividualAudio && this.pdfSlots) {
			this.slotList = this.pdfSlots
				.sort((slotA, slotB) => slotA.mediaSlot - slotB.mediaSlot)
				.map(slot => this.$format(MediaSlotBinding[slot.mediaSlot]));

			this.slotSelected = this.slotList[0];
			await this.displayContentChanged(this.slotList[0]);
		}
	}

	async displayContentChanged(mediaSlot) {
		if (this.slotSelected !== this.previousSlot && this.isMediaUploaded) {
			this.showWarning = true;
		}

		if (!this.isMediaUploaded) {
			this.slotSelected = mediaSlot;
			mediaSlot = this.$format(MediaNameToSlotMap[mediaSlot]);

			await ContentService.GetIndividualAudio(this.currentComponentId, mediaSlot)
				.then((data: PageAndAudioMediaResponseModel) => {
					if (data) {
						this.pageAndAudio = data.pageAudio;
						const remainingFiles = this.pageAndAudio.pageAudioMediaList.filter(pa => !this.allFileItem?.some(f => /*f.id === pa.individualAudioMediaId ||*/ f.pageId === pa.pageId));
						remainingFiles.forEach(file => {
							this.allFileItem?.push(this.getInitialFileObj(file, "en", false));
						});
					}
				})
				.catch((err) => {

				});
		}
	}
}
